import { useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "./auth-context";
import { LanguageSwitcher } from "./LanguageSwitcher";
import { meetsPasswordRequirements, type PasswordUpdateError } from "./password-policy";

const messageKey: Record<PasswordUpdateError, string> = {
  password_requirements: "auth.passwordRequirementsError",
  password_same: "auth.passwordSameError",
  recovery_session_missing: "auth.recoveryLinkInvalid",
  password_update_failed: "auth.passwordUpdateError",
};

export function ResetPasswordPage({ invalid = false }: { invalid?: boolean }) {
  const { t } = useTranslation();
  const { updatePassword } = useAuth();
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [message, setMessage] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (password !== confirmPassword) {
      setMessage(t("auth.passwordMismatch"));
      return;
    }
    if (!meetsPasswordRequirements(password)) {
      setMessage(t("auth.passwordRequirementsError"));
      return;
    }
    setBusy(true);
    const error = await updatePassword(password);
    setBusy(false);
    setMessage(error ? t(messageKey[error]) : t("auth.passwordUpdated"));
  };

  return <main className="auth-page"><section className="auth-card" aria-labelledby="reset-password-title">
    <div className="auth-actions"><LanguageSwitcher /></div>
    <h1 id="reset-password-title">{t("auth.choosePassword")}</h1>
    {invalid ? <>
      <p className="lead">{t("auth.recoveryLinkInvalid")}</p>
      <a className="primary-button" href="/">{t("auth.requestNewRecoveryLink")}</a>
    </> : <>
    <p className="lead">{t("auth.passwordRequirements")}</p>
    <form onSubmit={(event) => void submit(event)}>
      <label htmlFor="new-password">{t("auth.newPassword")}</label>
      <input id="new-password" type="password" autoComplete="new-password" minLength={12} required value={password} onChange={(event) => setPassword(event.target.value)} />
      <label htmlFor="confirm-password">{t("auth.confirmPassword")}</label>
      <input id="confirm-password" type="password" autoComplete="new-password" minLength={12} required value={confirmPassword} onChange={(event) => setConfirmPassword(event.target.value)} />
      {message && <p className="form-message" role="status">{message}</p>}
      <button className="primary-button" disabled={busy} type="submit">{busy ? t("auth.working") : t("auth.updatePassword")}</button>
    </form>
    </>}
  </section></main>;
}
